/**
 * 브라우저에서 마커(`.mind`)를 컴파일한다 — **라이브 폴백 경로**.
 *
 * 🚨 왜 서버가 아니라 브라우저인가. MindAR 컴파일러는 TensorFlow.js 위에 있어 **WebGL** 을
 *    요구하고, Node 설치 경로는 `node-canvas` 네이티브 빌드에서 막힌다(2026-08-27 실측).
 *    배치 결정(A+C)에 따라 **백필은 로컬 Playwright**, **라이브는 이 파일**이 맡는다.
 *    prod Render 에 브라우저를 상주시키는 안(B)은 채택하지 않았다.
 *
 * 🔑 컴파일러는 CDN 에서 **버전을 박아** 가져온다. 마커 포맷이 바뀌면 이미 인쇄되어 나간
 *    키링이 안 읽히기 때문에, 자동 최신(`@latest`)을 쓰지 않는다.
 */

const MINDAR_URL = "https://cdn.jsdelivr.net/npm/mind-ar@1.2.5/dist/mindar-image.prod.js";

interface MindArCompiler {
  compileImageTargets(
    images: readonly HTMLImageElement[],
    onProgress: (percent: number) => void,
  ): Promise<unknown>;
  exportData(): Promise<ArrayBuffer | Uint8Array>;
}

interface MindArModule {
  Compiler: new () => MindArCompiler;
}

type ModuleLoader = (url: string) => Promise<MindArModule>;

/** 기본 로더. 검사에서 갈아끼울 수 있도록 인자로 뺐다. */
const importModule: ModuleLoader = (url) =>
  import(/* @vite-ignore */ url) as Promise<MindArModule>;

export class MarkerCompileError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "MarkerCompileError";
    if (cause !== undefined) this.cause = cause;
  }
}

export interface CompileOptions {
  onProgress?: (percent: number) => void;
  loadModule?: ModuleLoader;
  decodeImage?: (source: Blob) => Promise<HTMLImageElement>;
  /** 검사에서 갈아끼운다. 기본 = 컴파일러(TensorFlow.js)가 쓰는 것과 같은 조건으로 WebGL 을 열어 본다. */
  hasWebGl?: () => boolean;
  /** 진행률이 이 시간 동안 안 움직이면 멈춘 것으로 본다. 기본 2분(정상 컴파일은 7~25초). */
  stallMs?: number;
  /** 검사에서 갈아끼운다. 기본 = window 의 error · unhandledrejection. */
  onUncaught?: (listener: (error: unknown) => void) => () => void;
}

const NO_WEBGL_MESSAGE =
  "이 컴퓨터는 그래픽 기능(WebGL)이 꺼져 있어서 AR 인식 파일을 만들 수 없어요. 다른 컴퓨터나 크롬에서 다시 해 주세요.";

/**
 * 🚨 «그냥 WebGL 이 되는가» 가 아니라 **컴파일러와 같은 조건**으로 본다.
 *    TensorFlow.js 는 `failIfMajorPerformanceCaveat: true` 로 문맥을 연다 — 소프트웨어 렌더링뿐인
 *    기기는 보통 WebGL 은 열리는데 이 조건에서만 거절된다. 그때 컴파일러는 CPU 로 내려가다
 *    `Kernel 'BinomialFilter' not registered for backend 'cpu'` 를 **잡히지 않는 오류로** 던지고,
 *    약속(Promise)은 끝나지 않아 화면이 «0%» 에서 영원히 멈춘다(2026-10-02 실측 · 헤드리스 크롬).
 */
function detectWebGl(): boolean {
  if (typeof document === "undefined") return false;
  const attributes = { failIfMajorPerformanceCaveat: true };
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2", attributes) ?? canvas.getContext("webgl", attributes));
  } catch {
    return false;
  }
}

function listenUncaught(listener: (error: unknown) => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const onError = (event: ErrorEvent) => listener(event.error ?? event.message);
  const onRejection = (event: PromiseRejectionEvent) => listener(event.reason);
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}

async function defaultDecode(source: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(source);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return image;
  } finally {
    // 🚨 즉시 해제하면 안 된다 — decode 가 끝난 뒤에도 컴파일러가 픽셀을 읽는다.
    //    한 프레임 뒤에 놓는다.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * 인쇄될 그림 → 마커 바이트.
 *
 * 🚨 «빈 마커» 를 조용히 통과시키지 않는다. 마커가 비면 스캔 화면은 카메라만 켜지고 아무 일도
 *    일어나지 않는데, 그때는 이미 종이가 인쇄되어 나간 뒤다.
 */
export async function compileMarker(
  image: Blob,
  options: CompileOptions = {},
): Promise<Uint8Array> {
  const load = options.loadModule ?? importModule;
  const decode = options.decodeImage ?? defaultDecode;
  if (!(options.hasWebGl ?? detectWebGl)()) throw new MarkerCompileError(NO_WEBGL_MESSAGE);
  let module: MindArModule;
  try {
    module = await load(MINDAR_URL);
  } catch (error) {
    throw new MarkerCompileError("마커 만들기 도구를 불러오지 못했어요. 인터넷 연결을 확인해 주세요.", error);
  }
  try {
    const element = await decode(image);
    const compiler = new module.Compiler();
    await guardStall(
      (onProgress) => compiler.compileImageTargets([element], onProgress),
      options,
    );
    const exported = await compiler.exportData();
    const bytes = exported instanceof Uint8Array ? exported : new Uint8Array(exported);
    if (bytes.byteLength < 1_024) {
      throw new MarkerCompileError(`마커가 비었어요(${bytes.byteLength}바이트).`);
    }
    return bytes;
  } catch (error) {
    if (error instanceof MarkerCompileError) throw error;
    throw new MarkerCompileError("그림에서 마커를 만들지 못했어요. 다른 장면으로 다시 해 보세요.", error);
  }
}

/**
 * 컴파일러가 약속을 끝내지 않고 멈추는 경우를 막는 안전망 — 위의 WebGL 사전 확인이 못 잡은 것까지.
 * 🔑 컴파일 중에 잡히지 않은 오류가 나거나 진행률이 오래 안 움직이면 실패로 끝낸다.
 *    컴파일러가 내부에서 던진 오류는 우리 try/catch 에 오지 않고 window 로 간다.
 */
function guardStall(
  run: (onProgress: (percent: number) => void) => Promise<unknown>,
  options: CompileOptions,
): Promise<unknown> {
  const stallMs = options.stallMs ?? 120_000;
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopListening: () => void = () => undefined;
    const finish = (action: () => void) => {
      clearTimeout(timer);
      stopListening();
      action();
    };
    const arm = () => {
      clearTimeout(timer);
      timer = setTimeout(() => finish(() => reject(new MarkerCompileError(
        "AR 인식 파일을 만드는 데 너무 오래 걸려요. 다른 컴퓨터나 크롬에서 다시 해 주세요.",
      ))), stallMs);
    };
    stopListening = (options.onUncaught ?? listenUncaught)((error) => finish(() => reject(new MarkerCompileError(
      "그림에서 마커를 만들지 못했어요. 다른 컴퓨터나 크롬에서 다시 해 주세요.",
      error,
    ))));
    arm();
    run((percent) => {
      arm();
      options.onProgress?.(percent);
    }).then(
      (value) => finish(() => resolve(value)),
      (error: unknown) => finish(() => reject(error)),
    );
  });
}

export const __testing = { MINDAR_URL, NO_WEBGL_MESSAGE };
